import { useState, useEffect } from 'react';
import { 
  Gift, 
  Copy, 
  Share2, 
  Users, 
  TrendingUp, 
  CheckCircle2,
  Clock
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { useToast } from '@/hooks/useToast';
import api from '@/lib/api';
import { Badge } from '@/components/ui/Badge';

export function IndiqueGanhe() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    carregarDados();
  }, []);

  const carregarDados = async () => {
    try {
      setLoading(true);
      const res = await api.get('/indicacao/meus-bonus');
      setData(res.data);
    } catch (error) {
      console.error('Erro ao carregar dados de indicação:', error);
    } finally {
      setLoading(false);
    }
  };

  const linkIndicacao = `${window.location.origin}/convite/${data?.codigo}`;

  const copyLink = () => {
    navigator.clipboard.writeText(linkIndicacao);
    toast({
      title: 'Link copiado!',
      description: 'Agora é só enviar para seus amigos.',
    });
  };

  const shareWhatsApp = () => {
    const text = `Olha que legal esses colecionáveis 3D da Fort3D! Se você usar meu link para a primeira compra, ganha um desconto especial e eu também ganho bônus: ${linkIndicacao}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  if (loading) return <div className="py-12 text-center text-gray-500">Carregando...</div>;

  return (
    <div className="py-12 px-4 max-w-4xl mx-auto space-y-8">
      <div className="text-center space-y-4">
        <div className="w-20 h-20 bg-fort3d-laranja/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Gift className="w-10 h-10 text-fort3d-laranja" />
        </div>
        <h1 className="text-4xl font-black text-gray-900">Indique e Ganhe</h1>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          Compartilhe a Fort3D com seus amigos. Quando eles fizerem a primeira compra, 
          você ganha <span className="font-bold text-emerald-600">400 pontos</span> (R$ 20,00 de crédito)!
        </p>
      </div>

      <Card className="border-2 border-fort3d-laranja/20 overflow-hidden shadow-xl">
        <CardHeader className="bg-fort3d-laranja/5">
          <CardTitle className="text-center">Seu Link de Indicação</CardTitle>
        </CardHeader>
        <CardContent className="p-8 space-y-6">
          <div className="flex gap-2">
            <Input 
              readOnly 
              value={linkIndicacao}
              className="bg-gray-50 font-mono text-sm"
            />
            <Button variant="outline" onClick={copyLink} title="Copiar" className="px-3">
              <Copy className="w-4 h-4" />
            </Button>
          </div>
          
          <Button 
            className="w-full bg-green-500 hover:bg-green-600 text-white flex items-center justify-center gap-2 h-12"
            onClick={shareWhatsApp}
          >
            <Share2 className="w-5 h-5" />
            Compartilhar no WhatsApp
          </Button>
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-fort3d-laranja" />
              Minhas Indicações
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {data?.indicacoes?.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">Você ainda não indicou ninguém.</p>
            ) : (
              data?.indicacoes?.map((ind: any) => (
                <div key={ind.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-sm font-bold text-gray-900">{ind.indicadoEmail}</p>
                    <p className="text-xs text-gray-500">{new Date(ind.createdAt).toLocaleDateString()}</p>
                  </div>
                  {ind.status === 'CONVERTIDO' ? (
                    <Badge variant="success" className="flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> Convertido
                    </Badge>
                  ) : (
                    <Badge variant="warning" className="flex items-center gap-1">
                      <Clock className="w-3 h-3" /> Pendente
                    </Badge>
                  )}
                </div>
              ))
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-600" />
              Resumo de Ganhos
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex justify-between items-end border-b pb-4">
              <div>
                <p className="text-sm text-gray-500">Saldo Atual (Fort3D Club)</p>
                <p className="text-3xl font-black text-fort3d-laranja">{data?.pontosAtuais} pts</p>
              </div>
              <p className="text-emerald-600 font-bold mb-1">
                R$ {(data?.pontosAtuais * 0.05).toFixed(2)} acumulados
              </p>
            </div>
            <div className="space-y-2">
              <p className="text-xs text-gray-400 uppercase tracking-widest font-bold">Como funciona?</p>
              <ul className="text-sm text-gray-600 space-y-2">
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 rounded-full bg-fort3d-laranja text-white text-[10px] flex items-center justify-center shrink-0 mt-0.5">1</span>
                  <span>Amigo clica no seu link e cadastra o e-mail.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 rounded-full bg-fort3d-laranja text-white text-[10px] flex items-center justify-center shrink-0 mt-0.5">2</span>
                  <span>Ele faz a primeira compra de qualquer valor.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 rounded-full bg-fort3d-laranja text-white text-[10px] flex items-center justify-center shrink-0 mt-0.5">3</span>
                  <span>Assim que o pagamento for confirmado, você ganha o bônus!</span>
                </li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
